import { useEffect, useRef, useState, useCallback } from 'react';
import { Eye, Trash2, Pencil, Check, X, Send, Loader2, AtSign } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../context/RealtimeContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import Avatar from './ui/avatar-initials';

/**
 * Reusable comment thread + presence indicator for any entity (expense,
 * coin_transfer, fine). Opens a presence session while mounted.
 *
 * Props:
 *  - entityType: 'expense' | 'coin_transfer' | 'fine'
 *  - entityId: string
 *  - compact?: boolean — hides empty-state when true (for dense panels)
 */
export default function CommentThread({ entityType, entityId, compact = false }) {
  const { user } = useAuth();
  const { onEvent } = useRealtime();
  const [comments, setComments] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, text}
  const [mentionables, setMentionables] = useState([]); // [{id, name}]
  const [mentionDropdown, setMentionDropdown] = useState(null); // {query, anchor} or null
  const inputRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypingAtRef = useRef(0);

  // Fetch + enter presence
  useEffect(() => {
    if (!entityType || !entityId) return undefined;
    let cancelled = false;

    const fetchAll = async () => {
      try {
        const [cRes, pRes, mRes] = await Promise.all([
          api.get(`/engagement/${entityType}/${entityId}/comments`),
          api.post('/presence/enter', { entity_type: entityType, entity_id: entityId }),
          api.get(`/engagement/${entityType}/${entityId}/mentionables`).catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          setComments(cRes.data);
          setViewers(pRes.data.viewers || []);
          setMentionables(mRes.data || []);
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    };
    fetchAll();

    // Heartbeat every 15s
    const hb = setInterval(() => {
      api.post('/presence/heartbeat', { entity_type: entityType, entity_id: entityId }).catch(() => {});
    }, 15000);

    return () => {
      cancelled = true;
      clearInterval(hb);
      api.post('/presence/leave', { entity_type: entityType, entity_id: entityId }).catch(() => {});
    };
  }, [entityType, entityId]);

  // SSE listeners
  useEffect(() => {
    return onEvent(
      ['comment_added', 'comment_edited', 'comment_deleted', 'presence_update'],
      (type, payload) => {
        if (payload.entity_type !== entityType || payload.entity_id !== entityId) return;
        if (type === 'presence_update') {
          setViewers(payload.viewers || []);
        } else if (type === 'comment_added') {
          setComments((prev) => prev.some((c) => c.id === payload.comment.id) ? prev : [...prev, payload.comment]);
        } else if (type === 'comment_edited') {
          setComments((prev) => prev.map((c) => c.id === payload.comment.id ? payload.comment : c));
        } else if (type === 'comment_deleted') {
          setComments((prev) => prev.filter((c) => c.id !== payload.comment_id));
        }
      }
    );
  }, [onEvent, entityType, entityId]);

  const pingTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingAtRef.current < 2000) return;
    lastTypingAtRef.current = now;
    api.post('/presence/typing', { entity_type: entityType, entity_id: entityId }).catch(() => {});
  }, [entityType, entityId]);

  // Detect @<query> right before cursor → open dropdown
  const handleTextChange = (newText, cursorPos) => {
    setText(newText);
    if (newText.trim()) pingTyping();
    const before = newText.slice(0, cursorPos);
    const m = before.match(/(^|\s)@(\S{0,30})$/);
    if (m && mentionables.length > 0) {
      setMentionDropdown({ query: m[2].toLowerCase(), startPos: cursorPos - m[2].length - 1 });
    } else {
      setMentionDropdown(null);
    }
  };

  const insertMention = (name) => {
    if (!mentionDropdown) return;
    const before = text.slice(0, mentionDropdown.startPos);
    const after = text.slice(mentionDropdown.startPos).replace(/^@\S*/, '');
    const next = `${before}@${name} ${after.replace(/^\s+/, '')}`;
    setText(next);
    setMentionDropdown(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const filteredMentions = mentionDropdown
    ? mentionables.filter((u) => u.id !== user?.id && u.name.toLowerCase().includes(mentionDropdown.query)).slice(0, 6)
    : [];

  // Render comment text with @mention chips
  const renderCommentText = (raw, mentions = []) => {
    if (!raw) return null;
    if (mentions.length === 0 && !raw.includes('@')) return raw;
    // Split by @<word> patterns, longest match first
    const names = mentionables.map((u) => u.name).sort((a, b) => b.length - a.length);
    if (names.length === 0) return raw;
    const re = new RegExp(`@(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`, 'gi');
    const parts = [];
    let lastIdx = 0;
    let match;
    while ((match = re.exec(raw)) !== null) {
      if (match.index > lastIdx) parts.push(raw.slice(lastIdx, match.index));
      const name = match[1];
      const isMe = user?.name?.toLowerCase() === name.toLowerCase();
      parts.push(
        <span
          key={`m-${match.index}`}
          className={`inline-block px-1.5 py-0 rounded font-semibold ${isMe ? 'bg-yellow-400/30 text-yellow-700 dark:text-yellow-300' : 'bg-primary/15 text-primary'}`}
        >
          @{name}
        </span>
      );
      lastIdx = match.index + match[0].length;
    }
    if (lastIdx < raw.length) parts.push(raw.slice(lastIdx));
    return parts;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setPosting(true);
    try {
      const res = await api.post(`/engagement/${entityType}/${entityId}/comments`, { text: t });
      setComments((prev) => [...prev, res.data]);
      setText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const t = editing.text.trim();
    if (!t) return;
    try {
      const res = await api.put(`/engagement/comments/${editing.id}`, { text: t });
      setComments((prev) => prev.map((c) => (c.id === res.data.id ? res.data : c)));
      setEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Edit failed');
    }
  };

  const deleteComment = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await api.delete(`/engagement/comments/${id}`);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const otherViewers = viewers.filter((v) => v.user_id !== user?.id);
  const typers = otherViewers.filter((v) => v.is_typing);

  return (
    <div className="space-y-3" data-testid={`comment-thread-${entityType}-${entityId}`}>
      {/* Presence bar */}
      {otherViewers.length > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/40 border border-border rounded-full px-3 py-1 w-fit" data-testid="presence-bar">
          <Eye className="w-3.5 h-3.5" />
          <span>
            Also viewing: <span className="font-semibold text-foreground">{otherViewers.map((v) => v.name).join(', ')}</span>
          </span>
        </div>
      )}

      {/* Thread */}
      <div className="space-y-2" data-testid="comment-list">
        {loading ? (
          <div className="text-xs text-muted-foreground">Loading comments…</div>
        ) : comments.length === 0 ? (
          !compact && <div className="text-xs text-muted-foreground italic">No comments yet. Start the discussion.</div>
        ) : (
          comments.map((c) => {
            const mine = c.user_id === user?.id;
            const canEdit = mine && (Date.now() - new Date(c.created_at).getTime() < 5 * 60 * 1000);
            return (
              <div key={c.id} className={`rounded-xl border p-2.5 text-sm ${mine ? 'bg-primary/5 border-primary/20' : 'bg-card'}`} data-testid={`comment-${c.id}`}>
                <div className="flex items-start gap-2">
                  <Avatar name={c.user_name} size="xs" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold text-xs">{c.user_name}{mine && ' (you)'}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(c.created_at).toLocaleString()}
                        {c.edited_at && <span className="ml-1 italic">(edited)</span>}
                      </span>
                    </div>
                {editing?.id === c.id ? (
                  <div className="flex gap-1 mt-1">
                    <Input
                      value={editing.text}
                      onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                      className="h-8 text-sm flex-1"
                      autoFocus
                    />
                    <Button size="icon" variant="ghost" onClick={saveEdit} className="h-8 w-8" data-testid={`edit-save-${c.id}`}><Check className="w-4 h-4 text-green-500" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setEditing(null)} className="h-8 w-8"><X className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ) : (
                  <>
                    <div className="whitespace-pre-wrap break-words mt-0.5">{renderCommentText(c.text, c.mentions)}</div>
                    {mine && (
                      <div className="flex gap-1 mt-1 justify-end">
                        {canEdit && (
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setEditing({ id: c.id, text: c.text })} data-testid={`edit-btn-${c.id}`}>
                            <Pencil className="w-3 h-3 mr-1" /> Edit
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-500" onClick={() => deleteComment(c.id)} data-testid={`delete-btn-${c.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </>
                )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Typing indicator */}
      {typers.length > 0 && (
        <div className="text-xs text-muted-foreground italic flex items-center gap-1" data-testid="typing-indicator">
          <span className="inline-flex gap-0.5">
            <span className="w-1 h-1 bg-primary rounded-full animate-bounce" />
            <span className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
            <span className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
          </span>
          {typers.map((t) => t.name).join(', ')} {typers.length === 1 ? 'is' : 'are'} typing…
        </div>
      )}

      {/* Composer */}
      <form onSubmit={handleSubmit} className="relative flex gap-2">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            value={text}
            onChange={(e) => handleTextChange(e.target.value, e.target.selectionStart)}
            onKeyDown={(e) => {
              if (mentionDropdown && filteredMentions.length > 0) {
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  insertMention(filteredMentions[0].name);
                } else if (e.key === 'Escape') {
                  setMentionDropdown(null);
                }
              }
            }}
            placeholder="Add a comment… use @ to mention"
            data-testid="comment-input"
            className="h-10 rounded-full pr-10"
            disabled={posting}
          />
          {mentionables.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const v = text + (text.endsWith(' ') || text === '' ? '@' : ' @');
                setText(v);
                setMentionDropdown({ query: '', startPos: v.length - 1 });
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
              data-testid="mention-trigger-btn"
              tabIndex={-1}
              title="Mention someone"
            >
              <AtSign className="w-4 h-4" />
            </button>
          )}
          {mentionDropdown && filteredMentions.length > 0 && (
            <div className="absolute bottom-full left-0 mb-1 w-full bg-popover border-2 border-border rounded-xl shadow-lg overflow-hidden z-50" data-testid="mention-dropdown">
              {filteredMentions.map((u, idx) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => insertMention(u.name)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center gap-2 ${idx === 0 ? 'bg-accent/50' : ''}`}
                  data-testid={`mention-option-${u.id}`}
                >
                  <AtSign className="w-3.5 h-3.5 text-primary" />
                  <span className="font-semibold">{u.name}</span>
                  {idx === 0 && <span className="ml-auto text-xs text-muted-foreground">↵ to insert</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button type="submit" size="icon" className="h-10 w-10 rounded-full shrink-0" disabled={posting || !text.trim()} data-testid="comment-submit">
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
}
